import { inject, Service } from '@angular/core';
import { catchError, combineLatest, first, from, fromEvent, map, mergeMap, Observable, of, ReplaySubject, switchMap, tap } from 'rxjs';
import { GRecaptcha } from '../models/grecaptcha.model';
import { CitadelConfigurationService } from '@citadel/core';

@Service()
export class RecaptchaService {
  private siteKey: string | null = null;
  private initialized = new ReplaySubject<void>(1);
  private config = inject(CitadelConfigurationService);

  //private recaptchaSite = '';

  public execute(action: string): Observable<string> {
    return this.initialized.pipe(
      first(),
      switchMap(() => this.executeRecaptcha(action))
    );
  }

  public initialize(): Observable<void> {
    return this.config.getCurrentBrand().pipe(
      mergeMap((brand) => {
        const cID = brand;
        return combineLatest([
          of(cID),

          this.config.getConfig("Citadel.Auth")
        ]);
      }),
      // Left `any[]`: the tuple is really [BrandConfig, IConfiguration] (getCurrentBrand/getConfig's
      // declared types), but the body reads config[1].wrappedObject, which IConfiguration does not
      // declare - typing the tuple properly turns this into a compile error, not a fix. Recorded as
      // a bug, not resolved: IConfiguration here does not describe what's actually on the wire.
      mergeMap((config: any[]) => {
        let recaptchaSite = '';
        if (config[1].wrappedObject.Recaptha && config[0] !== '') {

          const fIndex = Object.keys(config[1].wrappedObject.Recaptha).findIndex(f => f === config[0].CompanyID.toString());
          if (fIndex !== -1) {

            const site: string = config[1].wrappedObject.Recaptha[Object.keys(config[1].wrappedObject.Recaptha)[fIndex]].SiteKey;
            recaptchaSite = site;
          }
        }
        return of({ recaptcha_site_key: recaptchaSite });
      }),
      tap((config) => {
        if (!config.recaptcha_site_key) {
          throw new Error('No site key returned from config API');
        }
        this.siteKey = config.recaptcha_site_key;
      }),
      switchMap((config) => {
        this.loadRecaptchaScript(config.recaptcha_site_key);
        return of(0 as any as void);
      }),
      catchError((err) => {
        console.error('reCAPTCHA initialization error:', err.message);
        this.initialized.error(err);
        throw err;
      })
    );
  }

  private loadRecaptchaScript(siteKey: string) {
    if (!siteKey) {
      throw new Error('Site key is undefined');
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    document.head.appendChild(script);
    fromEvent(script, 'load')
      .pipe(first())
      .subscribe({
        next: () => {
          const grecaptcha = (window as any).grecaptcha as
            | GRecaptcha
            | undefined;
          if (!grecaptcha || !grecaptcha.ready) {
            this.initialized.error(
              new Error('reCAPTCHA not loaded after script load')
            );
            return;
          }
          grecaptcha.ready(() => this.initialized.next());
          
        },
        error: (err) => {
          throw new Error(`Failed to load reCAPTCHA script: ${ err.message}`);
        },
      });
  }
  private executeRecaptcha(action: string): Observable<string> {
    const grecaptcha = (window as any).grecaptcha as GRecaptcha | undefined;
    if (!grecaptcha || !grecaptcha.execute) {
      throw new Error('reCAPTCHA not loaded');
    }
    if (!this.siteKey) {
      throw new Error('Site key not initialized');
    }
    return from(grecaptcha.execute(this.siteKey, { action })).pipe(
      map((token) => token as string),
      catchError((err) => {
        throw new Error(
          `Failed to execute reCAPTCHA for action ${action}: ${err.message}`
        );
      })
    );
  }
}
